
/**
 * LandingPage.jsx — LUMINA v3.7 "雪山之光 · 深度升华版"
 * Jack 视觉优化建议全面落地
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FirstVisitBanner from "./FirstVisitBanner";
import { track } from "../utils/track";
import { API } from "../constants/config";
import OracleResult from "./OracleResult";
import DharmaRecommendCard from "./DharmaRecommendCard";

// ── Oracle hint rules (shared with OracleLanding) ──
const HINT_RULES = [
  { keywords: ["梦见","梦到","做了个梦","噩梦","梦里","昨晚梦"], hint: "💭 将使用：周公解梦（259条词典 · 秒级响应）" },
  { keywords: ["起名","取名","宝宝名","测名","改名"], hint: "✍️ 将使用：五格剖象起名（笔画数理 · 五行补缺）" },
  { keywords: ["今天","今日","黄历","宜忌","吉日","黄道"], hint: "📅 将使用：老黄历（节气+建除十二神）" },
  { keywords: ["抽签","求签","灵签","观音"], hint: "🙏 将使用：观音灵签（100签 · 签诗解读）" },
  { keywords: ["我的命","命盘","格局","一辈子","天赋","命运","命格","宿命"], hint: "🔮 将使用：八字+紫微（需要您的生辰信息）" },
  { keywords: ["会不会","能不能","结果","顺利吗","成功吗","合适吗","要不要","签约","面试","展会","生意","投资","运势","运程"], hint: "⚡ 将使用：六爻起卦（无需生辰 · 3秒出结果）" },
  { keywords: ["修行","法门","密法","本尊","灌顶","修持","修什么","气脉","拙火","前行","度母","普巴","上师","那若","法会","扎龙","仁波切","荟供","闭关","禅修","法讯","朗日"], hint: "☸️ 将使用：密法修行建议（命理→本尊·五根柱子框架）" },
];
function detectHint(text) {
  for (const rule of HINT_RULES) {
    if (rule.keywords.some(kw => text.includes(kw))) return rule.hint;
  }
  if (text.length > 3) return "⚡ 将使用：六爻起卦（无需生辰 · 3秒出结果）";
  return "";
}

// ─── 循环 placeholder ───────────────────────────────────────────────────────
const PLACEHOLDERS = [
  "🔮 八字推流年：我明年适合换工作吗？",
  "🌌 紫微斗数：这段感情是否有正缘？",
  "⚡ 六爻起卦：去上海参展能接到大单吗？",
  "🌀 奇门遁甲：新办公室选哪个方位最旺财？",
];

const ANIM = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&family=Noto+Serif+SC:wght@300;400;600;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&display=swap');

  @keyframes fadeUp  { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes pulse   {
    0%,100%{text-shadow:0 0 50px rgba(230,195,70,.7),0 2px 12px rgba(0,0,0,.8)}
    50%    {text-shadow:0 0 100px rgba(230,195,70,1.0),0 2px 12px rgba(0,0,0,.8)}
  }
  @keyframes bounce  { 0%,100%{transform:translateX(-50%) translateY(0)} 50%{transform:translateX(-50%) translateY(9px)} }
  @keyframes shimmer {
    0%  {background-position:200% center}
    100%{background-position:-200% center}
  }
  @keyframes rotateSlow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

  .lp-title { animation: pulse 5s ease-in-out infinite; }

  .lp-a1 { animation: fadeUp .9s ease forwards; }
  .lp-a2 { animation: fadeUp 1.2s ease forwards; }
  .lp-a3 { animation: fadeUp 1.5s ease forwards; }
  .lp-a4 { animation: fadeUp 1.8s ease forwards; }

  .lp-card { transition: transform .35s ease, box-shadow .35s ease; cursor:pointer; }
  .lp-card:hover { transform: translateY(-12px); }

  .lp-btn { transition: all .25s ease; }
  .lp-btn:hover { transform: translateY(-2px); }

  .hero-input::placeholder { color: rgba(255,255,255,.45); opacity:1; }
  .hero-input:focus::placeholder { color: rgba(255,255,255,.25); }

  .lp-btn-shine::after {
    content:''; position:absolute; inset:0; border-radius:inherit;
    background:linear-gradient(90deg,transparent 0%,rgba(0,0,0,.15) 50%,transparent 100%);
    background-size:200% 100%; animation:shimmer 2.8s linear infinite;
  }

  .lp-nav-btn { transition:all .2s ease; }
  .lp-nav-btn:hover { opacity:.8; transform:translateY(-1px); }

  .lp-feat { transition:background .25s ease, border-color .25s ease; }
  .lp-feat:hover { background:rgba(6,78,59,.04) !important; border-color:rgba(212,175,55,.25) !important; }

  .lp-scroll { animation:bounce 2.4s ease-in-out infinite; }

  @keyframes spin-gear {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes spin-gear-rev {
    from { transform: rotate(0deg); }
    to   { transform: rotate(-360deg); }
  }
  @keyframes float-card {
    0%,100% { transform: translateY(0px); }
    50%     { transform: translateY(-8px); }
  }
  @keyframes glow-pulse {
    0%,100% { box-shadow: 0 0 20px rgba(212,175,55,.18), 0 0 0 1px rgba(212,175,55,.25); }
    50%     { box-shadow: 0 0 40px rgba(212,175,55,.35), 0 0 0 1px rgba(212,175,55,.40); }
  }
  .te-gear-outer { animation: spin-gear 18s linear infinite; transform-origin: center; }
  .te-gear-inner { animation: spin-gear-rev 12s linear infinite; transform-origin: center; }
  .te-card-float { animation: float-card 4s ease-in-out infinite; }
  .te-card-glow  { animation: glow-pulse 3s ease-in-out infinite; }

  /* Card hover glows */
  .lp-blue:hover   { box-shadow:0 20px 60px rgba(8,145,178,.22),0 8px 32px rgba(0,0,0,.06) !important; }
  .lp-purple:hover { box-shadow:0 20px 60px rgba(168,85,247,.20),0 8px 32px rgba(0,0,0,.06) !important; }
  .lp-amber:hover  { box-shadow:0 20px 60px rgba(212,175,55,.22),0 8px 32px rgba(0,0,0,.06) !important; }
`;

/* ── SVG Icons ── */
const Icon = {
  pillars:(
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="9" width="5" height="17" rx="1.5"/>
      <rect x="11" y="4" width="5" height="22" rx="1.5"/>
      <rect x="19" y="12" width="5" height="14" rx="1.5"/>
      <line x1="1" y1="28" x2="31" y2="28"/>
    </svg>
  ),
  elements:(
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="16" cy="16" r="6"/>
      <line x1="16" y1="2" x2="16" y2="10"/>
      <line x1="16" y1="22" x2="16" y2="30"/>
      <line x1="2" y1="16" x2="10" y2="16"/>
      <line x1="22" y1="16" x2="30" y2="16"/>
      <line x1="6" y1="6" x2="11.5" y2="11.5"/>
      <line x1="20.5" y1="20.5" x2="26" y2="26"/>
    </svg>
  ),
  timeline:(
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,23 8,14 13,19 20,7 28,12"/>
      <circle cx="20" cy="7" r="2.5" fill="currentColor" stroke="none"/>
      <line x1="2" y1="28" x2="30" y2="28"/>
      <line x1="8" y1="28" x2="8" y2="25"/>
      <line x1="16" y1="28" x2="16" y2="25"/>
      <line x1="24" y1="28" x2="24" y2="25"/>
    </svg>
  ),
  moon:(
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M21 6a11 11 0 1 1-11 19A13 13 0 0 0 21 6z"/>
      <circle cx="25" cy="9" r="1.5" fill="currentColor" stroke="none"/>
      <circle cx="28" cy="15" r="1" fill="currentColor" stroke="none"/>
    </svg>
  ),
  lotus:(
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M16 28s-8-5-8-13a8 8 0 0 1 8-8"/>
      <path d="M16 28s 8-5  8-13a8 8 0 0 0-8-8"/>
      <path d="M16 7c0 0-5 4-5 9s5 12 5 12"/>
      <path d="M16 7c0 0 5 4 5 9s-5 12-5 12"/>
      <line x1="16" y1="28" x2="16" y2="31"/>
    </svg>
  ),
  mail:(
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="26" height="18" rx="2"/>
      <polyline points="3,7 16,18 29,7"/>
      <path d="M3 25l8-9M29 25l-8-9"/>
    </svg>
  ),
};

/* ── Portal Card ── */
function PortalCard({ lang, accentColor, gradFrom, gradTo, glassColor, borderColor,
                      cardClass, icon, titleZh, titleEn, tagZh, tagEn,
                      pointsZh, pointsEn, btnZh, btnEn, onClick, soon }) {
  const zh = lang === "zh";
  const navigate = useNavigate();
  return (
    <div className={`lp-card ${cardClass}`} onClick={onClick} style={{
      background: glassColor,
      backdropFilter: "blur(28px) saturate(1.7)",
      WebkitBackdropFilter: "blur(24px) saturate(1.6)",
      border: `1px solid ${borderColor}`,
      borderRadius: 24,
      padding: "48px 32px 40px",
      flex: "1 1 300px", maxWidth: 370,
      position: "relative", overflow: "hidden",
    }}>
      {/* top shimmer line */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:1,
        background:`linear-gradient(90deg,transparent,${accentColor}80,transparent)`}}/>

      {/* Coming soon tag */}
      {soon && (
        <div style={{position:"absolute",top:20,right:20,
          fontSize:9,fontWeight:700,letterSpacing:".18em",
          color:"#718096",background:"rgba(0,0,0,.05)",
          padding:"4px 12px",borderRadius:9999,border:"1px solid rgba(0,0,0,.10)"}}>
          COMING SOON
        </div>
      )}

      {/* Icon */}
      <div style={{
        width:60,height:60,borderRadius:16,marginBottom:24,
        background:`linear-gradient(135deg,${gradFrom}22,${gradTo}18)`,
        border:`1px solid ${accentColor}30`,
        display:"flex",alignItems:"center",justifyContent:"center",
        color:accentColor,fontSize:28,
      }}>{icon}</div>

      {/* Title */}
      <div style={{fontSize:23,fontWeight:600,color:"#1A202C",
        fontFamily:"'Noto Serif SC',serif",letterSpacing:".04em",marginBottom:4}}>
        {zh ? titleZh : titleEn}
      </div>
      <div style={{fontSize:11,color:"#A0AEC0",letterSpacing:".2em",
        textTransform:"uppercase",marginBottom:10,fontFamily:"'Cormorant Garamond',serif"}}>
        {zh ? titleEn : titleZh}
      </div>

      {/* Tag */}
      <div style={{
        display:"inline-block",fontSize:11,color:accentColor,
        background:`${accentColor}15`,border:`1px solid ${accentColor}30`,
        padding:"4px 14px",borderRadius:9999,marginBottom:22,letterSpacing:".06em",
      }}>
        {zh ? tagZh : tagEn}
      </div>

      {/* Bullet points */}
      <ul style={{listStyle:"none",margin:"0 0 28px",padding:0}}>
        {(zh ? pointsZh : pointsEn).map((p, i) => (
          <li key={i} style={{
            fontSize:13,color:"#4A5568",lineHeight:1.8,
            paddingLeft:14,position:"relative",
          }}>
            <span style={{position:"absolute",left:0,color:accentColor,fontSize:9,top:6}}>◆</span>
            {p}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button className="lp-btn lp-btn-shine" onClick={onClick} style={{
        position:"relative",
        background: soon
          ? "rgba(0,0,0,.06)"
          : "linear-gradient(135deg,#D4AF37,#9A7B20)",
        border: soon
          ? `1px solid ${borderColor}`
          : "1px solid rgba(212,175,55,0.6)",
        color: soon ? "rgba(0,0,0,.3)" : "#fff",
        padding:"12px 28px",borderRadius:9999,
        fontSize:13,fontWeight:700,cursor: soon ? "default" : "pointer",
        letterSpacing:".04em",
        boxShadow: soon ? "none" : "0 6px 24px rgba(212,175,55,0.35)",
      }}>
        {zh ? btnZh : btnEn}
      </button>
    </div>
  );
}

/* ── Feature row ── */
function Feat({ zh, icon, accent, titleZh, titleEn, descZh, descEn, badgeZh, badgeEn }) {
  return (
    <div className="lp-feat" style={{
      display:"flex",gap:18,alignItems:"flex-start",
      padding:"22px 20px",borderRadius:16,
      border:"1px solid rgba(0,0,0,.07)",
      background:"rgba(255,255,255,.80)",
    }}>
      <div style={{
        flexShrink:0,width:52,height:52,borderRadius:14,
        background:`${accent}12`,border:`1px solid ${accent}20`,
        display:"flex",alignItems:"center",justifyContent:"center",color:accent,
      }}>{icon}</div>
      <div style={{flex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:5}}>
          <span style={{fontSize:14,fontWeight:700,color:"#1A202C",
            fontFamily:"'Inter','Montserrat',sans-serif"}}>
            {zh ? titleZh : titleEn}
          </span>
          {(badgeZh || badgeEn) && (
            <span style={{fontSize:10,color:accent,background:`${accent}18`,
              padding:"2px 10px",borderRadius:9999,border:`1px solid ${accent}28`,
              letterSpacing:".06em",fontWeight:700}}>
              {zh ? badgeZh : badgeEn}
            </span>
          )}
        </div>
        <div style={{fontSize:12.5,color:"#5A6478",lineHeight:1.7}}>
          {zh ? descZh : descEn}
        </div>
      </div>
    </div>
  );
}

/* Phase 54B: DynamicProofCounter removed — fake metrics */

/* ── Main ── */
export default function LandingPage({ lang="en", setPage, setLang, user }) {
  const [scrolled, setScrolled] = useState(false);
  const [glow, setGlow] = useState({x:50,y:50});
  const zh = lang === "zh";
  const navigate = useNavigate();
  // ── Oracle state ──
  const oracleRef = React.useRef(null);
  const composingRef = React.useRef(false);  // true = IME composing, textarea uncontrolled
  const [query, setQuery] = React.useState("");
  const [hint, setHint] = React.useState("");
  const [oracleLoading, setOracleLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState("");

  // 循环 placeholder
  const [placeholderIdx, setPlaceholderIdx] = React.useState(0);
  const [todayEnergy, setTodayEnergy] = React.useState(null);
  React.useEffect(() => {
    const t = setInterval(() => setPlaceholderIdx(i => (i + 1) % PLACEHOLDERS.length), 3000);
    return () => clearInterval(t);
  }, []);
  // Fetch today's energy data from backend (real-time, no auth required)
  React.useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/public/today-energy`);
        if (r.ok) setTodayEnergy(await r.json());
      } catch (_) { /* keep hardcoded fallback */ }
    })();
  }, []);

  // Safety: if compositionStart fired but compositionEnd never did, auto-reset composing flag
  React.useEffect(() => {
    if (!composingRef.current) return;
    const t = setTimeout(() => { composingRef.current = false; }, 4000);
    return () => clearTimeout(t);
  });

  const handleOracleSubmit = React.useCallback(async (q = query, forcedModule = "auto") => {
    const text = (q || query).trim(); if (!text) return;
    setOracleLoading(true); setError(""); setResult(null);
    try {
      const token = localStorage.getItem("lumina_token");
      const headers = token ? {"Content-Type":"application/json","Authorization":`Bearer ${token}`} : {"Content-Type":"application/json"};
      const resp = await fetch(`${API}/api/v4/omni/query`, {
        method:"POST",headers,
        body:JSON.stringify({query_text:text,wx_id:"",forced_module:forcedModule}),
      });
      if (!resp.ok) { const e=await resp.json().catch(()=>({})); throw new Error(e.detail||`请求失败(${resp.status})`); }
      setResult(await resp.json());
    } catch(e) { setError(e.message); }
    finally { setOracleLoading(false); }
  }, [query]);

  useEffect(()=>{
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  },[]);

  useEffect(()=>{ track("landing_view", { lang }); },[]);

  // Prevent browser from restoring old scroll position on hard-refresh
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  const HERO = "/kailash.jpg";


  return (
    <div style={{minHeight:"100vh",background:"#F9F7F2",
      color:"#1A202C",fontFamily:"'Inter','Montserrat','Cormorant Garamond',Georgia,sans-serif",
      overflowX:"hidden"}}>
      <style>{ANIM}</style>
      <FirstVisitBanner setPage={setPage} lang={lang} />

      {/* ── NAV ── */}
      <nav style={{
        position:"fixed",top:0,left:0,right:0,zIndex:200,
        display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"0 52px",height:66,
        background: scrolled ? "rgba(253,251,247,.96)" : "transparent",
        backdropFilter: scrolled ? "blur(20px) saturate(1.4)" : "none",
        borderBottom: scrolled ? "1px solid rgba(0,0,0,.08)" : "none",
        transition:"all .4s ease",
      }}>
        <div onClick={()=>window.scrollTo({top:0,behavior:"smooth"})}
          style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
          <img src="/logo_transparent.png" alt="澄光能量"
               style={{height:36,width:"auto",display:"block",
                       filter:scrolled?"none":"brightness(1.1)"}}
               onError={e=>{ e.target.style.display='none'; }}/>
          <span style={{fontSize:16,fontWeight:400,color:scrolled?"#8B6914":"#d4c070",letterSpacing:".12em",
              fontFamily:"'Cormorant Garamond',serif"}}>
            澄光命盘 · LUMINAARC
          </span>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {/* Nav links: Chart | Relationships | 🔮 Ask | Plans | ☸ */}
          <button className="lp-nav-btn" onClick={()=>setPage("audit")}
            style={{background:"transparent",border:"none",
              color:scrolled?"#5C4A1E":"rgba(212,175,55,.82)",padding:"6px 14px",borderRadius:6,
              fontSize:12,cursor:"pointer",letterSpacing:".04em",fontWeight:500,
              fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
            {zh?"命盘":"Chart"}
          </button>
          <button className="lp-nav-btn" onClick={()=>navigate("/synastry")}
            style={{background:"transparent",border:"none",
              color:scrolled?"#5C4A1E":"rgba(212,175,55,.82)",padding:"6px 14px",borderRadius:6,
              fontSize:12,cursor:"pointer",letterSpacing:".04em",fontWeight:500,
              fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
            {zh?"关系":"Relationships"}
          </button>
          <button className="lp-nav-btn" onClick={()=>navigate("/oracle")}
            style={{background:"transparent",border:"none",
              color:scrolled?"#5C4A1E":"rgba(212,175,55,.82)",padding:"6px 14px",borderRadius:6,
              fontSize:12,cursor:"pointer",letterSpacing:".04em",fontWeight:500,
              fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
            {zh?"🔮问卜":"🔮 Ask"}
          </button>
          <button className="lp-nav-btn" onClick={()=>navigate("/pricing")}
            style={{background:"transparent",border:"none",
              color:scrolled?"#5C4A1E":"rgba(212,175,55,.82)",padding:"6px 14px",borderRadius:6,
              fontSize:12,cursor:"pointer",letterSpacing:".04em",fontWeight:500,
              fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
            {zh?"计划":"Plans"}
          </button>
          <button className="lp-nav-btn" onClick={()=>navigate("/vajra")}
            style={{background:"transparent",border:"none",
              color:"rgba(184,134,11,0.45)",fontSize:12,letterSpacing:".05em",
              padding:"6px 10px",borderRadius:6,cursor:"pointer",
              fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic"}}
            onMouseOver={e=>e.target.style.color="rgba(184,134,11,0.78)"}
            onMouseOut={e=>e.target.style.color="rgba(184,134,11,0.45)"}>
            ☸
          </button>

          <button className="lp-nav-btn" onClick={()=>setLang(zh?"en":"zh")}
            style={{background:"rgba(212,175,55,.12)",border:"1px solid rgba(212,175,55,.28)",
              color:scrolled?"#8B6914":"#d4c070",padding:"5px 14px",borderRadius:9999,
              fontSize:11,cursor:"pointer",letterSpacing:".1em"}}>
            {zh ? "EN" : "中文"}
          </button>

          {user ? (
            <button className="lp-nav-btn" onClick={()=>setPage("dashboard")}
              style={{background:"transparent",border:scrolled?"1px solid rgba(139,105,20,.35)":"1px solid rgba(212,175,55,.4)",
                color:scrolled?"#5C4A1E":"rgba(212,175,55,.85)",padding:"7px 20px",borderRadius:9999,
                fontSize:12,cursor:"pointer",letterSpacing:".04em",fontWeight:500}}>
              {zh ? "日课" : "Dashboard"}
            </button>
          ) : (
            <>
              <button className="lp-nav-btn" onClick={()=>setPage("login")}
                style={{background:"transparent",border:scrolled?"1px solid rgba(139,105,20,.35)":"1px solid rgba(139,105,20,.28)",
                  color:scrolled?"#5C4A1E":"rgba(139,105,20,.85)",padding:"7px 20px",borderRadius:9999,
                  fontSize:12,cursor:"pointer",letterSpacing:".04em",fontWeight:500}}>
                {zh ? "登录" : "Sign In"}
              </button>
              <button className="lp-nav-btn" onClick={()=>setPage("register")}
                style={{background:"#064E3B",color:"#fff",border:"none",
                  padding:"8px 18px",borderRadius:9999,fontSize:13,fontWeight:600,
                  cursor:"pointer",letterSpacing:".04em",
                  fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei','Inter','Montserrat',sans-serif"}}>
                {zh?"免费注册":"Register free"}
              </button>
            </>
          )}
        </div>
      </nav>

      {/* ══ HERO ══ */}
      <section
        onMouseMove={e=>{
          const r=e.currentTarget.getBoundingClientRect();
          setGlow({x:Math.round((e.clientX-r.left)/r.width*100),y:Math.round((e.clientY-r.top)/r.height*100)});
        }}
        style={{
          position:"relative",minHeight:"100vh",
          display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",overflow:"hidden",
        }}>
        {/* BG image */}
        <div style={{
          position:"absolute",inset:0,zIndex:0,
          backgroundImage: `url(${HERO})`,
          backgroundSize:"cover",backgroundPosition:"center 58%",
          filter:"brightness(1.08) saturate(1.18) contrast(1.04)",
          pointerEvents:"none",
        }}/>
        {/* Overlay 1: subtle top darkening for text readability; bottom transparent for lake */}
        <div style={{position:"absolute",inset:0,zIndex:1,
          background:"linear-gradient(180deg,rgba(0,0,0,.20) 0%,rgba(0,0,0,.10) 40%,rgba(0,0,0,.02) 65%,transparent 85%)",
          pointerEvents:"none"}}/>
        {/* Overlay 2: golden horizon bloom */}
        <div style={{position:"absolute",inset:0,zIndex:2,
          background:"radial-gradient(ellipse 85% 46% at 50% 60%,rgba(230,180,40,.26) 0%,rgba(200,130,20,.08) 52%,transparent 70%)",
          pointerEvents:"none"}}/>

        {/* Overlay 4: cursor glow — 日照金山 */}
        <div style={{
          position:"absolute",inset:0,zIndex:4,pointerEvents:"none",
          background:`radial-gradient(circle 580px at ${glow.x}% ${glow.y}%,rgba(212,175,55,0.11) 0%,rgba(180,130,20,0.05) 45%,transparent 70%)`,
          transition:"background 0.18s ease",
        }}/>

        {/* Content */}
        <div style={{position:"relative",zIndex:10,textAlign:"center",padding:"0 24px",maxWidth:800}}>

          {/* Main headline */}
          <h1 className="lp-title lp-a2" style={{
            fontSize:"clamp(32px,5vw,56px)",
            fontWeight:600,color:"#fff",letterSpacing:zh?".12em":".04em",
            lineHeight:1.3,marginBottom:8,
            fontFamily:zh?"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif":"'Cormorant Garamond',serif",
            textShadow:"0 2px 24px rgba(0,0,0,.7)",
          }}>
            {zh ? "你不需要懂命理" : "You don't need to understand metaphysics"}
          </h1>
          <div className="lp-a2" style={{
            fontSize:"clamp(28px,4.5vw,48px)",
            fontWeight:600,color:"#D4AF37",
            letterSpacing:zh?".12em":".04em",
            fontFamily:zh?"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif":"'Cormorant Garamond',serif",
            textShadow:"0 2px 20px rgba(0,0,0,.7)",
            marginBottom:36,
          }}>
            {zh ? "你只需要问问题" : "You only need to ask questions"}
          </div>

          {/* ── Oracle 输入框 ── */}
          <div className="lp-a3" id="hero-oracle" style={{maxWidth:560,margin:"0 auto 16px"}}>
            <div style={{
              background:"rgba(0,0,0,.35)",backdropFilter:"blur(12px)",
              border:"1px solid rgba(212,175,55,.30)",borderRadius:14,
              padding:"14px 16px",boxShadow:"0 4px 32px rgba(0,0,0,.25)",
            }}>
              <textarea
                value={composingRef.current ? undefined : query}
                onChange={e=>{setQuery(e.target.value);setHint(detectHint(e.target.value));if(result)setResult(null);setError("");}}
                onCompositionStart={()=>{composingRef.current=true;}}
                onCompositionEnd={e=>{composingRef.current=false;setQuery(e.target.value);setHint(detectHint(e.target.value));if(result)setResult(null);setError("");}}
                onBlur={()=>{composingRef.current=false;}}
                onKeyDown={e=>{if(e.nativeEvent.isComposing)return;if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();handleOracleSubmit();}}}
                placeholder={zh ? PLACEHOLDERS[placeholderIdx] : "e.g. Will my next career move succeed?"}
                rows={2}
                ref={oracleRef}
                className="hero-input"
                style={{
                  width:"100%",border:"none",background:"transparent",
                  color:"#fff",fontSize:16,resize:"none",outline:"none",
                  fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif",
                }}
              />
              {hint && (
                <div style={{fontSize:12,color:"rgba(212,175,55,.85)",marginTop:8,fontWeight:500}}>
                  {hint}
                </div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10}}>
                <div style={{fontSize:13,color:"rgba(212,175,55,.85)",fontWeight:500}}>
                  {zh?"🔮 引擎真算 · 不做模板拼接":"🔮 Real engines · Not template generators"}
                </div>
                <button
                  onClick={() => handleOracleSubmit()}
                  disabled={oracleLoading}
                  style={{
                    background:oracleLoading?"#6B7280":"linear-gradient(135deg,#D4AF37,#064E3B)",
                    color:"#fff",border:"none",borderRadius:8,
                    padding:"10px 28px",fontSize:14,fontWeight:600,cursor:oracleLoading?"default":"pointer",
                    letterSpacing:".04em",
                    fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif",
                  }}>
                  {oracleLoading?"…":zh?"提问 →":"Ask →"}
                </button>
              </div>
            </div>
            {error && <div style={{color:"#FCA5A5",fontSize:12,marginTop:8}}>{error}</div>}
            {result && (
              <div style={{marginTop:12,textAlign:"left"}}>
                <OracleResult result={result} originalQuery={query} onFollowUp={(q,fm)=>{setQuery(q);handleOracleSubmit(q,fm);oracleRef.current?.scrollIntoView({behavior:"smooth",block:"center"});}} onNavigate={navigate} onNewQuestion={()=>{setResult(null);setError("");setTimeout(()=>oracleRef.current?.scrollIntoView({behavior:"smooth",block:"center"}),100);}}/>
                {!result.error && <DharmaRecommendCard query={query} />}
              </div>
            )}
          </div>

          {/* Trust bar */}
          <div className="lp-a4" style={{
            display:"flex",justifyContent:"center",gap:24,flexWrap:"wrap",
            fontSize:12,color:"rgba(255,255,255,.55)",
            fontFamily:"'Inter',sans-serif",letterSpacing:".03em",
          }}>
            <span>⭐ {zh?"4.8 用户评分":"4.8 rating"}</span>
            <span>🔒 {zh?"端到端加密":"End-to-end encrypted"}</span>
            <span>🆓 {zh?"免费使用 · 无需登录":"Free · No login"}</span>
          </div>
        </div>


        {/* Phase 54B: 社会证明数据移除，待 Phase 55 收集真实用户反馈后恢复 */}

        {/* Scroll arrow */}
        <div className="lp-scroll" style={{
          position:"absolute",bottom:32,left:"50%",
          color:"rgba(212,180,80,.4)",fontSize:20,zIndex:10,cursor:"pointer",
        }} onClick={()=>document.getElementById("value-props").scrollIntoView({behavior:"smooth"})}>↓</div>
      </section>

      {/* Phase 54C: 价值主张 — 精简版 */}
      <section id="value-props" style={{padding:"56px 24px",background:"#FDFBF7"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"center",gap:48,flexWrap:"wrap",textAlign:"center"}}>
            {[
              {icon:"🔍",zh:"看清自己",en:"See Yourself",descZh:"八字+紫微解析能量特质",descEn:"Bazi + Ziwei energy blueprint"},
              {icon:"🤝",zh:"理解关系",en:"Understand Relationships",descZh:"合盘分析共鸣与张力",descEn:"Synastry reveals resonance"},
              {icon:"🧭",zh:"找到方向",en:"Find Direction",descZh:"六爻定事·奇门择时",descEn:"I Ching + Qimen guidance"},
            ].map((p,i)=>(
              <div key={i} style={{maxWidth:220}}>
                <div style={{fontSize:36,marginBottom:12}}>{p.icon}</div>
                <div style={{fontSize:18,fontWeight:600,color:"#1A202C",marginBottom:4,
                  fontFamily:"'Noto Serif SC',serif",letterSpacing:".04em"}}>
                  {zh?p.zh:p.en}
                </div>
                <div style={{fontSize:13,color:"#64748B",lineHeight:1.6}}>
                  {zh?p.descZh:p.descEn}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ══ 今日能量示范 ══ */}
      <section style={{padding:"60px 24px",background:"linear-gradient(180deg,#FDFBF7 0%,#F9F7F2 100%)"}}>
        <div style={{maxWidth:720,margin:"0 auto",textAlign:"center"}}>
          <div style={{fontSize:10,letterSpacing:".45em",color:"rgba(212,175,55,.75)",
            textTransform:"uppercase",marginBottom:16,fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic"}}>
            {zh ? "今日能量" : "Today's Energy"}
          </div>
          <h2 style={{fontSize:"clamp(20px,3vw,30px)",fontWeight:300,
            color:"rgba(0,0,0,.82)",fontFamily:"'Cormorant Garamond',serif",letterSpacing:".08em",marginBottom:8}}>
            {zh ? "实时排盘 · 每日更新" : "Real-time Chart · Daily Update"}
          </h2>
          <p style={{fontSize:14,color:"#64748B",marginBottom:28,lineHeight:1.7}}>
            {zh ? "以下数据由排盘引擎实时计算，每日更新。输入你的生辰，即可获取专属能量分析。"
               : "Data calculated in real-time by our charting engine. Enter your birth info for personalized analysis."}
          </p>
          <div style={{background:"rgba(255,255,255,.82)",border:"1px solid rgba(212,175,55,.20)",
            borderRadius:16,padding:"28px 24px",boxShadow:"0 4px 24px rgba(6,78,59,.06)",
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:16,textAlign:"left"}}>
            {[
              {icon:"📅",label:zh?"今日日期":"Date",
               value: todayEnergy ? (zh ? todayEnergy.date_zh : todayEnergy.date_en) : (zh?"6月7日 · 周日":"Jun 7 · Sun")},
              {icon:"🌿",label:zh?"节气":"Solar Term",
               value: todayEnergy ? (zh ? todayEnergy.solar_term_display_zh : todayEnergy.solar_term_display_en) : (zh?"芒种":"Grain in Ear")},
              {icon:"🔥",label:zh?"当令五行":"Ruling Element",
               value: todayEnergy ? (zh ? todayEnergy.ruling_element_zh : todayEnergy.ruling_element_en) : (zh?"巳火司令":"Si Fire")},
              {icon:"⏰",label:zh?"吉时":"Auspicious",
               value: todayEnergy ? (zh ? todayEnergy.auspicious_zh : todayEnergy.auspicious_en) : (zh?"卯(5-7) 午(11-13) 酉":"Mao · Wu · You")},
              {icon:"⚠️",label:zh?"冲煞":"Clash",
               value: todayEnergy ? (zh ? todayEnergy.clash_zh : todayEnergy.clash_en) : (zh?"冲羊(未) · 煞西南":"Goat · Southwest")},
            ].map((item,i)=>(
              <div key={i} style={{padding:"12px 14px",background:"rgba(249,247,242,.6)",borderRadius:10}}>
                <div style={{fontSize:20,marginBottom:6}}>{item.icon}</div>
                <div style={{fontSize:11,color:"#94A3B8",marginBottom:3}}>{item.label}</div>
                <div style={{fontSize:14,color:"#1E293B",fontWeight:500}}>{item.value}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:28}}>
            <button onClick={()=>navigate("/audit")} style={{
              background:"linear-gradient(135deg,#D4AF37,#064E3B)",color:"#fff",
              border:"none",borderRadius:12,padding:"14px 36px",fontSize:15,fontWeight:600,
              cursor:"pointer",letterSpacing:".04em",
              fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
              {zh ? "输入生辰 · 查看专属能量 →" : "Enter Birth · See Your Energy →"}
            </button>
            <div style={{fontSize:11,color:"#94A3B8",marginTop:8}}>
              {zh ? "免费注册 · 无需信用卡" : "Free signup · No credit card"}
            </div>
          </div>
        </div>
      </section>


      {/* ══ 免费易学工具 ══ */}
      <section style={{padding:"72px 24px",background:"#FDFBF7"}}>
        <div style={{maxWidth:960,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <div style={{fontSize:10,letterSpacing:".45em",color:"rgba(212,175,55,.75)",
              textTransform:"uppercase",marginBottom:16,fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic"}}>
              {zh ? "免费易学工具" : "Free Tools"}
            </div>
            <h2 style={{fontSize:"clamp(22px,3.5vw,36px)",fontWeight:300,
              color:"rgba(0,0,0,.82)",fontFamily:"'Cormorant Garamond',serif",letterSpacing:".08em"}}>
              {zh ? "无需登录 · 全部免费" : "No Login · 100% Free"}
            </h2>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:14}}>
            {[
              {href:"/tools?tab=almanac",icon:"📅",zh:"老黄历",en:"Almanac",zhDesc:"每日宜忌·28星宿·黄道黑道",enDesc:"Daily auspices · 28 Mansions"},
              {href:"/tools?tab=dreams",icon:"💭",zh:"解梦",en:"Dreams",zhDesc:"周公解梦原典·现代心理学",enDesc:"Zhougong + Modern Psychology"},
              {href:"/tools?tab=divination",icon:"🏮",zh:"观音灵签",en:"Oracle",zhDesc:"100签·诗意解曰仙机",enDesc:"100 Lots · Full Dataset"},
              {href:"/tools?tab=naming",icon:"✨",zh:"AI起名",en:"Naming",zhDesc:"八字五行补缺·康熙笔画",enDesc:"Bazi + Kangxi Strokes"},
            ].map((t,i) => (
              <div key={i} onClick={()=>{navigate(t.href);window.scrollTo(0,0);}} style={{
                display:"block",padding:"24px 20px",borderRadius:14,
                background:"rgba(255,255,255,.7)",border:"1px solid rgba(212,175,55,.15)",
                textDecoration:"none",transition:"all .3s ease",cursor:"pointer",
              }}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(212,175,55,.12)";e.currentTarget.style.borderColor="rgba(212,175,55,.35)"}}
              onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";e.currentTarget.style.borderColor="rgba(212,175,55,.15)"}}
              >
                <div style={{fontSize:32,marginBottom:10}}>{t.icon}</div>
                <div style={{fontSize:16,fontWeight:600,color:"#2C2416",marginBottom:4}}>{zh?t.zh:t.en}</div>
                <div style={{fontSize:12,color:"rgba(0,0,0,.45)",lineHeight:1.5}}>{zh?t.zhDesc:t.enDesc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{padding:"36px 24px",background:"#FDFBF7",
        borderTop:"1px solid rgba(0,0,0,.06)",textAlign:"center"}}>
        {/* Footer links */}
        <div style={{display:"flex",justifyContent:"center",gap:28,flexWrap:"wrap",marginBottom:18}}>
          {[
            {label:zh?"定价":"Pricing", page:"pricing"},
            {label:zh?"关于":"About", page:"about"},
            {label:zh?"Vajra社群":"Vajra", page:"vajra"},
            {label:zh?"隐私":"Privacy", page:"privacy"},
          ].map(link=>(
            <button key={link.label} onClick={()=>link.action?link.action():setPage(link.page)} style={{
              background:"none",border:"none",cursor:"pointer",
              color:"rgba(0,0,0,.40)",fontSize:12,fontWeight:400,
              letterSpacing:".04em",
              fontFamily:"'Inter','Noto Sans SC',sans-serif",
            }}>{link.label}</button>
          ))}
        </div>
        <div style={{fontSize:15,fontWeight:300,color:"#8B6914",
          letterSpacing:".3em",marginBottom:8,fontFamily:"'Cormorant Garamond',serif"}}>
          ✦ &nbsp; 澄光命盘 · LUMINAARC &nbsp; ✦
        </div>
        <div style={{fontSize:11,color:"rgba(0,0,0,.35)",letterSpacing:".06em"}}>
          {zh
            ? "© 2026 澄光命盘 · 澄光映照，自见本性 · Toronto"
            : "© 2026 LUMINAARC · 澄光映照，自见本性 · Toronto"}
        </div>
      </footer>
    </div>
  );
}
